import { Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
  UploadIconDialogComponent,
  UploadIconDialogResult,
} from '@app/sharedModule/components/upload-icon-dialog/upload-icon-dialog.component';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators,
  UntypedFormGroup,
} from '@angular/forms';
import { DropdownOption } from '@app/storybook/components/dropdown/dropdown.component';
import {
  OrderProtectionFormShape,
  OrderProtectionModalSection,
  ORDER_PROTECTION_ICON_URLS,
  ORDER_PROTECTION_ICON_LABELS,
  ORDER_PROTECTION_LIST_ICON_URLS,
  ORDER_PROTECTION_LIST_ICON_LABELS,
  ORDER_PROTECTION_STARTER_TEMPLATE,
  ORDER_PROTECTION_VARIABLES,
  createOrderProtectionListItem,
} from './order-protection.model';
import { readDisplayConditionsGroup } from '../../components/smart-cart-form-display-conditions/display-conditions.model';

const MODAL_SECTION_LABELS: Record<OrderProtectionModalSection, string> = {
  headline: 'Headline',
  body: 'Body',
  list: 'List Item (optional)',
};

const ALLOWED_PRODUCT_IMAGE_TYPES = ['image/png', 'image/svg+xml', 'image/jpeg'];
const MAX_PRODUCT_IMAGE_BYTES = 100 * 1024;

// The Custom URL is merchant-entered and gets written into storefront markup (the copyable fallback link),
// so validate the scheme and HTML-attribute-encode it before interpolating — a `javascript:` scheme or a
// stray quote must never end up as executable/injected markup a shopper session hits. Only an absolute
// `https://` URL or a site-relative path is accepted — plain `http://` is rejected because a checkout
// destination over http is never secure. A site-relative path must begin with a single `/`: browsers strip
// tab/newline/CR and treat backslashes as forward slashes, so `//host`, `/\host` and `/<TAB>/host` all
// resolve to a `//host` network-path reference on another origin — collapse those first so a value that looks
// like a same-site path but navigates off-site can't slip through.
const isSafeEmbedUrl = (url: string): boolean => {
  const normalized = url.trim().toLowerCase();
  if (normalized.startsWith('https://')) {
    return true;
  }
  const collapsed = normalized.replace(/[\t\n\r]/g, '').replace(/\\/g, '/');
  return collapsed.startsWith('/') && !collapsed.startsWith('//');
};
const encodeHtmlAttr = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/'/g, '&#39;');

// Validate the Custom URL form control against the exact same predicate the snippet uses, so a value that
// can't be safely embedded can't be saved either (no regex that could drift from isSafeEmbedUrl). A blank
// value is caught by Validators.required; this covers unsafe/off-site values.
const safeCustomUrlValidator = (control: AbstractControl): ValidationErrors | null =>
  isSafeEmbedUrl((control.value ?? '').toString()) ? null : { unsafeUrl: true };

type OrderProtectionTab = 'settings' | 'appearance' | 'display-conditions';

@Component({
  selector: 'smart-cart-form-order-protection-editor',
  templateUrl: './order-protection.component.html',
  styleUrls: ['./order-protection.component.scss'],
  standalone: false,
})
export class SmartCartFormOrderProtectionEditorComponent implements OnInit {
  @Input({ required: true }) formGroup!: FormGroup<OrderProtectionFormShape>;
  @Input() usageToken = '';
  // The standalone form renders its own "Add with code" card, so it can hide the one here.
  @Input() showEmbedCard = true;

  readonly customCodeVariables = ORDER_PROTECTION_VARIABLES;
  readonly customCodeStarterTemplate = ORDER_PROTECTION_STARTER_TEMPLATE;
  readonly allowedProductImageTypes = ALLOWED_PRODUCT_IMAGE_TYPES;
  readonly maxProductImageBytes = MAX_PRODUCT_IMAGE_BYTES;

  readonly iconUrls = ORDER_PROTECTION_ICON_URLS;
  readonly iconLabels = ORDER_PROTECTION_ICON_LABELS;
  readonly listIconUrls = ORDER_PROTECTION_LIST_ICON_URLS;
  readonly listIconLabels = ORDER_PROTECTION_LIST_ICON_LABELS;

  readonly tabs: { label: string; value: OrderProtectionTab }[] = [
    { label: 'Settings', value: 'settings' },
    { label: 'Appearance', value: 'appearance' },
    { label: 'Display Conditions', value: 'display-conditions' },
  ];
  readonly pricingTypeOptions: DropdownOption[] = [
    { label: 'Fixed', value: 'fixed' },
    { label: 'Percentage', value: 'percent' },
  ];
  readonly routingOptions: DropdownOption[] = [
    { label: 'Automatic', value: 'automatic' },
    { label: 'Custom URL', value: 'custom_url' },
  ];
  // Read-only, copyable embed snippet shown in the "Add widget to cart" card.
  readonly embedControl = new FormControl<string>('', { nonNullable: true });

  activeTab: OrderProtectionTab = 'settings';
  pricingExpanded = true;
  designExpanded = true;
  cardDesignExpanded = true;
  continueDesignExpanded = true;

  isPercent = false;
  isQuickCheckout = false;
  isModal = false;
  productImageUrl = '';
  imageValidationError: string | null = null;

  tooltipInitial = '';
  modalHeadlineInitial = '';
  modalBodyInitial = '';
  modalFooterInitial = '';
  continueWithoutInitial = '';

  private readonly destroyRef = inject(DestroyRef);
  private readonly dialog = inject(MatDialog);

  get controls(): OrderProtectionFormShape {
    return this.formGroup.controls;
  }

  // The selected "Selecting method" — used to render each option's config inline after its own radio.
  get selectingType(): string {
    return this.controls.selectingMethod.controls.type.value;
  }

  // The embed textarea auto-sizes via CDK (cdkAutosizeMinRows), which only recomputes on user input — a
  // programmatic setValue (when the mode/routing changes the snippet) doesn't re-trigger it, so the box would
  // collapse to the min. Drive min-rows off the snippet's own line count (clamped to the textarea's maxRows)
  // so the full tag stays visible whenever the snippet is regenerated.
  get embedRows(): number {
    const lineCount = (this.embedControl.value || '').split('\n').length;
    return Math.min(20, Math.max(2, lineCount));
  }

  ngOnInit(): void {
    const c = this.formGroup.controls;

    this.isPercent = c.pricingMethod.value === 'percent';
    this.isQuickCheckout = c.selectingMethod.controls.type.value === 'quick_checkout';
    this.isModal = c.info.controls.type.value === 'modal';
    this.productImageUrl = c.productImageBase64.value;
    this.tooltipInitial = c.info.controls.tooltipContent.value;
    this.modalHeadlineInitial = c.info.controls.modalHeadline.value;
    this.modalBodyInitial = c.info.controls.modalBody.value;
    this.modalFooterInitial = c.info.controls.footer.value;
    this.continueWithoutInitial = c.quickCheckout.controls.continueWithout.controls.content.value;
    this.embedControl.setValue(this.buildEmbedSnippet());
    // Custom URL is required + must be a valid URL only while Custom-URL routing is selected, so a blank /
    // invalid Custom URL can't be saved (which would otherwise silently route shoppers to Shopify checkout).
    this.syncCustomUrlValidator(c.quickCheckout.controls.routing.value);

    c.pricingMethod.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((method) => (this.isPercent = method === 'percent'));
    c.selectingMethod.controls.type.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((type) => {
      this.isQuickCheckout = type === 'quick_checkout';
      // The embed snippet carries a fallback checkout only in quick-checkout mode — recompute on change.
      this.embedControl.setValue(this.buildEmbedSnippet());
    });
    // The quick-checkout fallback link points at the configured destination, so keep the snippet in sync
    // when the routing mode or the custom URL changes.
    c.quickCheckout.controls.routing.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((routing) => {
      this.syncCustomUrlValidator(routing);
      this.embedControl.setValue(this.buildEmbedSnippet());
    });
    c.quickCheckout.controls.customUrl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.embedControl.setValue(this.buildEmbedSnippet()));
    c.info.controls.type.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((type) => (this.isModal = type === 'modal'));
    c.productImageBase64.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => (this.productImageUrl = value));
  }

  // Apply the required + URL-pattern validator to customUrl only while Custom-URL routing is selected.
  private syncCustomUrlValidator(routing: string): void {
    const urlControl = this.formGroup.controls.quickCheckout.controls.customUrl;
    if (routing === 'custom_url') {
      urlControl.setValidators([Validators.required, safeCustomUrlValidator]);
    } else {
      urlControl.clearValidators();
    }
    urlControl.updateValueAndValidity({ emitEvent: false });
  }

  private buildEmbedSnippet(): string {
    const quick = this.formGroup.controls.quickCheckout.controls;
    // Quick-checkout + Custom URL: the widget's own button navigates to a custom URL, so the zero-JS fallback
    // is a LINK to that URL (a native checkout <button> can only submit to Shopify's checkout). Keyed off the
    // routing mode alone (not "&& customUrl") so a blank URL never silently falls through to the button form.
    if (this.isQuickCheckout && quick.routing.value === 'custom_url') {
      const raw = quick.customUrl.value.trim();
      // Only emit a merchant URL that's an ordinary web URL, HTML-attribute-encoded so it can't break out of
      // the href / inject markup. Blank or unsafe → default to Shopify's checkout with a prompt to fix it.
      const valid = raw.length > 0 && isSafeEmbedUrl(raw);
      const href = valid ? encodeHtmlAttr(raw) : '/checkout';
      const lines = [`<PDQOrderProtection token='${this.usageToken}'>`];
      if (!valid) {
        lines.push(
          `  <!-- Set a valid Custom URL (https:// or /path) in the widget settings above; using Shopify's checkout until then. -->`,
        );
      }
      lines.push(`  <a href="${href}" data-pdq-fallback>Checkout</a>`);
      lines.push(`</PDQOrderProtection>`);
      return lines.join('\n');
    }
    // Every other mode wraps the merchant's OWN Shopify checkout button inside the tag — Order Protection is
    // added when the shopper clicks it. Quick + Automatic additionally REPLACES the button with the widget's
    // own, so it marks the button `data-pdq-fallback` (hidden until the widget takes over, restored on
    // failure). Toggle/Checkbox KEEP the button visible, so it must NOT be `data-pdq-fallback` (that would
    // hide-then-reveal it → a flash).
    return this.buildCheckoutButtonSnippet(this.isQuickCheckout);
  }

  // Snippet that wraps the merchant's native checkout button. `withFallback` adds `data-pdq-fallback` (quick
  // mode, where the widget replaces the button). The example is Horizon's button; only `name="checkout"` (and
  // `data-pdq-fallback` when present) are fixed — class / form / label are adapted to the merchant's theme.
  private buildCheckoutButtonSnippet(withFallback: boolean): string {
    const note = withFallback
      ? [
          `  <!-- Move your theme's checkout button inside this tag and add data-pdq-fallback to it — the`,
          `       widget replaces it with its own button. (Example is Horizon — match class/form/label.) -->`,
        ]
      : [
          `  <!-- Move your theme's checkout button inside this tag (keep name="checkout"). Order Protection is`,
          `       added when the shopper clicks it; the button stays as-is. (Example is Horizon — match class/form/label.) -->`,
        ];
    return [
      `<PDQOrderProtection token='${this.usageToken}'>`,
      ...note,
      `  <button`,
      `    type="submit"`,
      `    id="checkout"`,
      `    class="cart__checkout-button button"`,
      `    name="checkout"`,
      ...(withFallback ? [`    data-pdq-fallback`] : []),
      `    {% if cart == empty %}`,
      `      disabled`,
      `    {% endif %}`,
      `    form="cart-form"`,
      `  >`,
      `    {{ 'content.checkout' | t }}`,
      `  </button>`,
      `</PDQOrderProtection>`,
    ].join('\n');
  }

  onTabChange(value: string): void {
    this.activeTab = value as OrderProtectionTab;
  }

  togglePricing(): void {
    this.pricingExpanded = !this.pricingExpanded;
  }
  toggleDesign(): void {
    this.designExpanded = !this.designExpanded;
  }
  toggleCardDesign(): void {
    this.cardDesignExpanded = !this.cardDesignExpanded;
  }
  toggleContinueDesign(): void {
    this.continueDesignExpanded = !this.continueDesignExpanded;
  }

  onTooltipChange(html: string): void {
    this.controls.info.controls.tooltipContent.setValue(html);
  }
  onModalHeadlineChange(html: string): void {
    this.controls.info.controls.modalHeadline.setValue(html);
  }
  onModalBodyChange(html: string): void {
    this.controls.info.controls.modalBody.setValue(html);
  }
  onModalFooterChange(html: string): void {
    this.controls.info.controls.footer.setValue(html);
  }
  onContinueWithoutChange(html: string): void {
    this.controls.quickCheckout.controls.continueWithout.controls.content.setValue(html);
  }

  onQuickCheckoutIcon(url: string | File): void {
    // The picker emits a preset URL, '' for "No icon", or the uploaded icon's data URL.
    if (typeof url === 'string') this.controls.quickCheckout.controls.icon.setValue(url);
  }

  onUploadQuickCheckoutIcon(): void {
    this.pickIcon((dataUrl) => {
      const quick = this.controls.quickCheckout.controls;
      quick.customIcon.setValue(dataUrl);
      quick.icon.setValue(dataUrl);
    });
  }

  onRemoveQuickCheckoutIcon(): void {
    const quick = this.controls.quickCheckout.controls;
    const wasSelected = !!quick.customIcon.value && quick.icon.value === quick.customIcon.value;
    quick.customIcon.setValue('');
    if (wasSelected) quick.icon.setValue('');
  }

  get listItems(): FormGroup[] {
    return this.controls.info.controls.listItems.controls as FormGroup[];
  }

  addListItem(): void {
    this.controls.info.controls.listItems.push(createOrderProtectionListItem());
  }
  removeListItem(index: number): void {
    this.controls.info.controls.listItems.removeAt(index);
  }
  setListItemIcon(index: number, icon: string | File): void {
    if (typeof icon === 'string') this.controls.info.controls.listItems.at(index).controls.icon.setValue(icon);
  }

  onUploadListItemIcon(index: number): void {
    this.pickIcon((dataUrl) => {
      const item = this.controls.info.controls.listItems.at(index).controls;
      item.customIcon.setValue(dataUrl);
      item.icon.setValue(dataUrl);
    });
  }

  onRemoveListItemIcon(index: number): void {
    const item = this.controls.info.controls.listItems.at(index).controls;
    const wasSelected = !!item.customIcon.value && item.icon.value === item.customIcon.value;
    item.customIcon.setValue('');
    if (wasSelected) item.icon.setValue('');
  }

  // The iconpicker only asks for an upload; opening the dialog and storing the result is the consumer's job.
  // Icons are kept as data URLs, the same way the product image is.
  private pickIcon(apply: (dataUrl: string) => void): void {
    this.dialog
      .open<UploadIconDialogComponent, void, UploadIconDialogResult | null>(UploadIconDialogComponent, {
        width: '576px',
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (!result?.file) return;
        const reader = new FileReader();
        reader.onload = () => apply(String(reader.result ?? ''));
        reader.readAsDataURL(result.file);
      });
  }

  // The modal's Headline / Body / List Item are reorderable sections (each with a drag handle).
  get modalSections(): OrderProtectionModalSection[] {
    return this.controls.info.controls.sectionOrder.value;
  }
  sectionLabel(section: OrderProtectionModalSection): string {
    return MODAL_SECTION_LABELS[section];
  }
  onSectionDrop(event: CdkDragDrop<OrderProtectionModalSection[]>): void {
    if (event.previousIndex === event.currentIndex) return;
    const order = [...this.modalSections];
    moveItemInArray(order, event.previousIndex, event.currentIndex);
    this.controls.info.controls.sectionOrder.setValue(order);
  }

  onProductImageChanged(file: File | null): void {
    this.imageValidationError = null;
    if (!file) {
      this.controls.productImageBase64.setValue('');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => this.controls.productImageBase64.setValue(String(reader.result ?? ''));
    reader.onerror = () => (this.imageValidationError = 'Could not read file. Please try again.');
    reader.readAsDataURL(file);
  }

  onProductImageValidationFailed(reason: 'type' | 'size'): void {
    this.imageValidationError =
      reason === 'type' ? 'Unsupported file type. Use PNG, SVG, or JPG.' : 'File too large. Max 100KB.';
  }

  get displayConditionsGroup(): UntypedFormGroup | null {
    return readDisplayConditionsGroup(this.formGroup);
  }
}
